import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'shared/components'
import { AsyncSelect } from 'shared/components/input/components/select'
import { INPUT_TYPES } from 'shared/components/input/input.types'
import styles from './styles'
import CustomIcon from '../customIcon'

const PASSWORD_INPUT_TYPE = 'password'

const Input = ({
  className,
  label,
  required,
  errorMessage,
  id,
  css,
  type,
  secondLabel,
  inlineIcon,
  grayBg,
  size = 'large',
  fluid,
  ...props
}) => {
  const [isFocused, setFocused] = useState(false)
  const inputRef = useRef()
  const [textVisibility, setTextVisibility] = useState(false)
  const inputId = id || props?.placeholder

  // If this is an async-select, render the AsyncSelect component
  if (type === INPUT_TYPES.ASYNC_SELECT) {
    return (
      <div css={[styles.container, css]} className={className}>
        {(label || required || secondLabel) && (
          <div css={styles.labelsContainer}>
            <label css={styles.label} htmlFor={inputId}>
              {label}
              {required && <span css={styles.required}>*</span>}
            </label>
            {secondLabel && <span css={styles.secondLabel}>{secondLabel}</span>}
          </div>
        )}
        <div css={styles.inputContainer}>
          <AsyncSelect
            id={inputId}
            showError={!!errorMessage}
            size={size}
            fluid={fluid}
            {...props}
          />
        </div>
        <span css={styles.errorMessage}>{errorMessage}</span>
      </div>
    )
  }

  // Regular input handling for non-async-select types
  const inputCss = [
    styles.input(size),
    errorMessage ? styles.inputError : styles.inputSuccess,
  ]

  const renderVisibilityToggle = () => {
    const isEmpty = !inputRef?.current?.value
    const shouldRenderToggle = type === PASSWORD_INPUT_TYPE && !isEmpty
    if (!shouldRenderToggle) return

    return (
      <Button
        plain
        // tabIndex={-1}
        css={styles.togglePasswordButton}
        onClick={() => setTextVisibility((prev) => !prev)}
      >
        <CustomIcon
          size={24}
          icon={textVisibility ? 'eyeOpen' : 'eye'}
          style={{ marginTop: 4 }}
        />
      </Button>
    )
  }

  const inputType = useMemo(
    () => (type === PASSWORD_INPUT_TYPE && textVisibility ? 'text' : type),
    [type, textVisibility]
  )
  return (
    <div css={[styles.container, css]} className={className}>
      {(label || required || secondLabel) && (
        <div css={styles.labelsContainer}>
          <label css={styles.label} htmlFor={inputId}>
            {label}
            {required && <span css={styles.required}>*</span>}
          </label>
          {secondLabel && <span css={styles.secondLabel}>{secondLabel}</span>}
        </div>
      )}
      <div
        css={[
          styles.inputContainer,
          grayBg && styles.inputGrayBg,
          isFocused && styles.inputContainerFocused,
        ]}
      >
        {inlineIcon && (
          <CustomIcon size={24} icon={inlineIcon} style={styles.inlineIcon} />
        )}
        <input
          css={inputCss}
          ref={inputRef}
          id={inputId}
          type={inputType}
          onFocus={() => setFocused(true)}
          onBlurCapture={() => setFocused(false)}
          {...props}
        />
        {renderVisibilityToggle()}
      </div>
      <span css={styles.errorMessage}>{errorMessage}</span>
    </div>
  )
}

Input.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  fluid: PropTypes.bool,
  errorMessage: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  css: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  type: PropTypes.string,
  secondLabel: PropTypes.shape({
    text: PropTypes.string,
    onClick: PropTypes.func,
  }),
  inlineIcon: PropTypes.string,
  grayBg: PropTypes.bool,
  size: PropTypes.string,
}

export default Input
